/*
# -----------------------------------------------------------------------------
#	Project: HSE Genius®
#	Client ID: 834215
#	Registration Code: IT-2025-ZR319-MQ744
#	Author: KaimakiWeb
#	Website: https://kaimakiweb.com
#	Copyright (c) 2024-2026 Kaimaki Web. All rights reserved.
#
#	This software and its source code are the property of KaimakiWeb.
#	Unauthorized copying, modification, distribution, or use of this software,
#	via any medium, is strictly prohibited unless prior written permission
#	is obtained from KaimakiWeb.
# -----------------------------------------------------------------------------
*/

import React, { createContext, useState, useEffect, useCallback } from "react";
import PaymentApi from "../services/api/payment";
import { isInviteAcceptanceUrl } from "../utils/invitation";

export const SubscriptionContext = createContext({
  hasActiveSubscription: false,
  isLoading: true,
  subscription: null,
  checkSubscription: () => {},
});

export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);

  const checkSubscription = useCallback(async () => {
    // Skip subscription check for invite acceptance URLs
    if (isInviteAcceptanceUrl()) {
      setIsLoading(false);
      setHasActiveSubscription(true); // Temporarily set to true for invite flow
      return;
    }

    setIsLoading(true);
    try {
      const subscriptionData = await PaymentApi.getSubscription();
      setSubscription(subscriptionData);

      // Check if subscription is paid
      const activeSubscription =
        subscriptionData &&
        subscriptionData.length > 0 &&
        subscriptionData[0].active;

      setHasActiveSubscription(activeSubscription);
    } catch (error) {
      // Avoid spamming console when backend is unreachable (ERR_CONNECTION_REFUSED, 504, etc.)
      const isNetworkError =
        error?.code === "ERR_NETWORK" ||
        error?.message?.includes("Network Error") ||
        error?.response?.status === 504;
      if (!isNetworkError) {
        console.error("Error fetching subscription:", error);
      }
      setHasActiveSubscription(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial check on mount only - no automatic refreshing
  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  return (
    <SubscriptionContext.Provider
      value={{
        hasActiveSubscription,
        subscription,
        isLoading,
        checkSubscription,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};
